import React, { ReactNode, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  Select,
  MenuItem,
  Collapse,
  SelectChangeEvent,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { ColDef, SortChangedEvent, GridApi } from 'ag-grid-community';
import { LoadingOverlay, FilterToggleButton } from '../../components';
import { PaginatedResponse, SortParams } from '../../types';

interface DataGridPageProps<T, F extends object> {
  // Page information
  readonly title: string;
  readonly addButtonText: string;
  readonly addButtonPath: string;
  readonly hideAddButton?: boolean; // New prop to control add button visibility

  // Grid data and config
  readonly columnDefs: ColDef[];
  readonly rowData?: T[];
  readonly response?: PaginatedResponse<T>;

  // Loading and error states
  readonly error?: unknown;

  // Pagination and filtering
  readonly pagination: {
    page: number;
    pageSize: number;
  };
  readonly filters: F;
  readonly filtersExpanded: boolean;
  readonly sort?: SortParams;

  // Container ref
  readonly gridContainerRef: React.RefObject<HTMLDivElement>;

  // Handlers
  readonly handlePageChange: (page: number) => void;
  readonly handlePageSizeChange: (event: SelectChangeEvent) => void;
  readonly handleSortChanged: (event: SortChangedEvent) => void;
  readonly handleNextPageHover: () => void;
  readonly setFiltersExpanded: (expanded: boolean) => void;

  // Custom elements
  readonly renderFilterControls: () => ReactNode;
  readonly navigate: (path: string) => void;
}

const DataGridPage = <T, F extends object>({
  title,
  addButtonText,
  addButtonPath,
  hideAddButton = false, // Default to showing the button
  columnDefs,
  rowData,
  response,
  error,
  pagination,
  filters,
  filtersExpanded,
  sort,
  gridContainerRef,
  handlePageChange,
  handlePageSizeChange,
  handleSortChanged,
  handleNextPageHover,
  setFiltersExpanded,
  renderFilterControls,
  navigate,
}: DataGridPageProps<T, F>) => {
  // Add a reference to the ag-Grid API
  const gridApiRef = useRef<GridApi | null>(null);

  // Apply the sort state to the grid when it changes and we're using server-side sorting
  useEffect(() => {
    if (gridApiRef.current && sort) {
      // Apply sort to the grid
      gridApiRef.current.applyColumnState({
        state: [
          {
            colId: sort.field,
            sort: sort.direction,
          },
        ],
        defaultState: { sort: null },
      });
    }
  }, [sort]);

  // Handle grid ready event to store the grid API
  const onGridReady = (params: { api: GridApi }) => {
    gridApiRef.current = params.api;

    // Apply initial sort if we're using server-side sorting and sort is defined
    if (sort) {
      params.api.applyColumnState({
        state: [
          {
            colId: sort.field,
            sort: sort.direction,
          },
        ],
        defaultState: { sort: null },
      });
    }
  };

  // Render pagination controls
  const renderPaginationControls = () => {
    if (!response) return null;

    const { page } = pagination;
    const totalPages = response.totalPages ?? 1;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          disabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        >
          Previous
        </Button>
        <Typography variant="body2" sx={{ mx: 2, fontSize: '0.75rem' }}>
          Page {page} of {totalPages}
        </Typography>
        <Button
          disabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
          onMouseEnter={handleNextPageHover}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        >
          Next
        </Button>
      </Box>
    );
  };

  return (
    <Box
      sx={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column', mt: 0, p: 0 }}
    >
      <LoadingOverlay />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
          height: 40,
          pt: 0,
        }}
      >
        <Typography variant="h6" component="h1" sx={{ fontSize: '1.1rem' }}>
          {title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Only render the Add button if hideAddButton is false */}
          {!hideAddButton && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => navigate(addButtonPath)}
              sx={{
                py: 0.5,
                height: 28,
                minHeight: 'unset',
                fontSize: '0.75rem',
              }}
            >
              {addButtonText}
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      {/* Filter Collapse Toggle */}
      <FilterToggleButton
        filtersExpanded={filtersExpanded}
        setFiltersExpanded={setFiltersExpanded}
        filters={filters}
      />

      {/* Filter Section - Collapsible */}
      <Collapse in={filtersExpanded}>
        <Paper elevation={0} variant="outlined" sx={{ p: 1, mb: 0.5 }}>
          {renderFilterControls()}
        </Paper>
      </Collapse>

      {/* Grid - Set to grow and fill available space */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          flexGrow: 1,
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        ref={gridContainerRef}
      >
        {error ? (
          <Typography color="error" sx={{ p: 2 }}>
            Error loading data. Please try again later.
          </Typography>
        ) : (
          <div
            className="ag-theme-material"
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <AgGridReact
              rowData={rowData ?? response?.data ?? []}
              columnDefs={columnDefs}
              pagination={false}
              animateRows={true}
              rowSelection="single"
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: true,
                comparator: () => 0,
              }}
              onSortChanged={handleSortChanged}
              onGridReady={onGridReady}
              suppressMultiSort={true}
              domLayout="normal"
              rowHeight={32}
              headerHeight={34}
            />
          </div>
        )}
      </Paper>

      {/* Footer with Pagination */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 0.5,
          height: 33,
          fontSize: '0.75rem',
        }}
      >
        {/* Left side - Pagination controls */}
        {renderPaginationControls()}

        {/* Right side - Contains both rows control and showing text */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Rows control */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ mr: 1, fontSize: '0.75rem' }}>
              Rows:
            </Typography>
            <Select
              value={pagination.pageSize.toString()}
              onChange={handlePageSizeChange}
              size="small"
              variant="outlined"
              sx={{
                minWidth: 55,
                height: 28,
                '.MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                  fontSize: '0.75rem',
                },
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        fontSize: '0.75rem',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>

          {/* Showing text */}
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            Showing {(pagination.page - 1) * pagination.pageSize + 1} -{' '}
            {Math.min(pagination.page * pagination.pageSize, response?.totalCount ?? 0)} of{' '}
            {response?.totalCount ?? 0}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DataGridPage;
